import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicAPI, communityAPI } from '../services/api';
import { ART_CATEGORIES } from '../utils/branding';
import { useAuth } from '../contexts/AuthContext';
import ArtistOfTheDay from '../components/ArtistOfTheDay';
import TrendingArtists from '../components/TrendingArtists';
import {
  Palette, Users, Landmark, Heart, ShieldCheck, Lock, Globe,
  ArrowRight, Star, MapPin, Search, Handshake, Zap, Image as ImageIcon, Eye,
} from 'lucide-react';

function SectionHeading({ eyebrow, title, subtitle, center = true }) {
  return (
    <div className={center ? 'text-center' : ''}>
      <span className={`eyebrow ${center ? 'justify-center' : ''}`}>{eyebrow}</span>
      <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-800">{title}</h2>
      {subtitle && <p className="mt-3 text-base md:text-lg text-navy-500 max-w-2xl mx-auto">{subtitle}</p>}
      {center && <div className="gold-divider mx-auto mt-6" />}
    </div>
  );
}

function HomePage() {
  const { isAuthenticated } = useAuth();
  const [stats, setStats] = useState({ total_artists: 0, completed_projects: 0, satisfaction_rate: 0 });
  const [featuredArtists, setFeaturedArtists] = useState([]);
  const [exhibitions, setExhibitions] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [discovery, setDiscovery] = useState({ most_viewed: [], most_celebrated_artist: null, recent_solds: [], recent_artists: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, artistsData, exhibitionsData, communitiesData, discoveryData] = await Promise.all([
          publicAPI.getStats().catch(() => ({ total_artists: 0, completed_projects: 0, satisfaction_rate: 0 })),
          publicAPI.getFeaturedArtists().catch(() => ({ artists: [] })),
          publicAPI.getExhibitions().catch(() => ({ exhibitions: [] })),
          publicAPI.getCommunities().catch(() => ({ communities: [] })),
          publicAPI.getDiscovery().catch(() => ({ most_viewed: [], most_celebrated_artist: null, recent_solds: [], recent_artists: [] })),
        ]);
        setStats(statsData);
        setFeaturedArtists(artistsData.artists || []);
        setExhibitions(exhibitionsData.exhibitions || []);
        setCommunities(communitiesData.communities || []);
        setDiscovery(discoveryData || {});
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleJoinCommunity = async (communityId) => {
    if (!isAuthenticated) {
      alert('Please login to join a community');
      return;
    }
    try {
      await communityAPI.join(communityId);
      alert('Successfully joined the community!');
    } catch (error) {
      alert(error.message || 'Failed to join community');
    }
  };

  const heroCards = [
    { Icon: ImageIcon, label: 'Paintings', href: '/paintings', desc: 'Browse original art' },
    { Icon: Users, label: 'Artists', href: '/artists', desc: 'Meet the makers' },
    { Icon: Landmark, label: 'Exhibitions', href: '/exhibitions', desc: 'Live & archived shows' },
    { Icon: Heart, label: 'Communities', href: '/communities', desc: 'Create & connect' },
  ];

  const trustBadges = [
    { Icon: ShieldCheck, title: 'Authentic Artists', sub: 'Verified & Trusted' },
    { Icon: Lock, title: 'Secure Payments', sub: 'Safe & Hassle-free' },
    { Icon: Globe, title: 'Global Reach', sub: 'Art Without Borders' },
    { Icon: Heart, title: 'Inspired Community', sub: 'Together We Create' },
  ];

  const howItWorks = [
    { step: 1, title: 'Find Your Artist', description: 'Browse our community of talented artisans', Icon: Search },
    { step: 2, title: 'Review Portfolio', description: 'Explore sample works, reviews and ratings', Icon: Palette },
    { step: 3, title: 'Discuss & Agree', description: 'Connect with the artist, discuss your vision', Icon: Handshake },
    { step: 4, title: 'Secure Payment', description: 'Pay securely and transparently through us', Icon: Zap },
  ];

  return (
    <div className="w-full bg-cream-100">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute top-[-6rem] right-[-6rem] w-[28rem] h-[28rem] bg-gold-500/10 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-[-8rem] left-[-6rem] w-[26rem] h-[26rem] bg-navy-800/5 rounded-full blur-3xl -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              <span className="eyebrow" data-testid="hero-eyebrow">Welcome to ChitraKalakar</span>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-semibold text-navy-800 leading-[1.05] tracking-tight">
                Let&apos;s Breathe Art
              </h1>
              <div className="w-24 h-[3px] bg-gold-500 rounded-full" />
              <p className="text-lg md:text-xl text-navy-500 leading-relaxed max-w-xl">
                Connect with talented artists. Commission meaningful artwork.
                Discover exhibitions. Celebrate creativity.
              </p>

              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-navy-400">I want to</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/signup" className="btn-gold" data-testid="hero-commission-btn">
                    <Palette className="w-5 h-5" strokeWidth={1.7} /> Commission Artwork
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </Link>
                  <Link to="/signup" className="btn-navy-outline" data-testid="hero-sell-btn">
                    <ImageIcon className="w-5 h-5" strokeWidth={1.7} /> Sell My Art
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-8 border-t border-gold-500/20 max-w-lg">
                {[
                  { value: stats.total_artists || 0, label: 'Talented Artists', Icon: Users },
                  { value: stats.completed_projects || 0, label: 'Completed Projects', Icon: Palette },
                  { value: `${stats.satisfaction_rate || 0}%`, label: 'Satisfaction Rate', Icon: Star },
                ].map((s, i) => (
                  <div key={i}>
                    <s.Icon className="w-5 h-5 text-gold-600 mb-2" strokeWidth={1.6} />
                    <p className="font-serif text-3xl sm:text-4xl font-semibold text-navy-800">{s.value}</p>
                    <p className="text-xs uppercase tracking-wider text-navy-400 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: clean 2x2 feature tiles (no background image) */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4 sm:gap-5">
                {heroCards.map((c) => (
                  <Link
                    key={c.label}
                    to={c.href}
                    className="card-classy group flex flex-col justify-between gap-6 p-5 sm:p-6 min-h-[140px] sm:min-h-[170px] lg:min-h-[196px]"
                    data-testid={`hero-card-${c.label.toLowerCase()}`}
                  >
                    <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-gold-500/12 text-gold-700 group-hover:bg-gold-500 group-hover:text-white transition-colors">
                      <c.Icon className="w-6 h-6" strokeWidth={1.5} />
                    </span>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="font-serif text-lg sm:text-xl font-semibold text-navy-800">{c.label}</span>
                        <ArrowRight className="w-4 h-4 text-gold-500 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" strokeWidth={2} />
                      </div>
                      <p className="text-xs sm:text-sm text-navy-400 mt-0.5">{c.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Trust badges */}
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6 rounded-2xl bg-white/70 border border-gold-500/15 p-6 sm:p-8">
            {trustBadges.map((b) => (
              <div key={b.title} className="flex items-center gap-3">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-navy-800/5 text-gold-600">
                  <b.Icon className="w-5 h-5" strokeWidth={1.6} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-navy-800">{b.title}</p>
                  <p className="text-xs text-navy-400">{b.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Artist of the Day */}
      <section className="lg:hidden py-8 bg-cream-200">
        <div className="max-w-lg mx-auto px-4">
          <ArtistOfTheDay />
        </div>
      </section>

      {/* Discover strip (desktop) */}
      <section className="hidden lg:block py-16 bg-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 gap-8">
          <ArtistOfTheDay />
          <TrendingArtists />
        </div>
      </section>

      {/* Discover: Most Viewed */}
      {discovery.most_viewed && discovery.most_viewed.length > 0 && (
        <section className="py-16 sm:py-20 bg-cream-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="Trending Now" title="Most Viewed Paintings" subtitle="The artworks capturing everyone's attention" />
            <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
              {discovery.most_viewed.slice(0, 4).map((art) => (
                <Link key={art.id} to={`/painting/${art.id}`} className="card-classy overflow-hidden group" data-testid={`most-viewed-${art.id}`}>
                  <div className="h-44 sm:h-52 bg-cream-200 overflow-hidden">
                    {(art.image || (art.images || [])[0]) ? (
                      <img src={art.image || art.images[0]} alt={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-10 h-10 text-navy-200" strokeWidth={1} /></div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-lg font-semibold text-navy-800 truncate group-hover:text-gold-600 transition-colors">{art.title}</h3>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-sm text-gold-600 font-medium">₹{Number(art.price || 0).toLocaleString('en-IN')}</span>
                      <span className="flex items-center gap-1 text-xs text-navy-400"><Eye className="w-3.5 h-3.5" strokeWidth={1.6} /> {art.views || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Discover: Most Celebrated Artist */}
      {discovery.most_celebrated_artist && (
        <section className="py-16 sm:py-20 bg-navy-800 text-white relative overflow-hidden">
          <div className="absolute top-[-4rem] left-[-4rem] w-80 h-80 bg-gold-500/10 rounded-full blur-3xl" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <span className="eyebrow !text-gold-400 justify-center">Hall of Fame</span>
            <h2 className="mt-4 text-center font-serif text-3xl md:text-4xl lg:text-5xl font-semibold">Most Celebrated Artist</h2>
            <div className="gold-divider mx-auto mt-6" />
            <div className="mt-10 flex flex-col sm:flex-row items-center gap-8 bg-white/5 border border-white/10 rounded-2xl p-8">
              <div className="w-28 h-28 rounded-full overflow-hidden ring-4 ring-gold-500/30 bg-navy-700 flex items-center justify-center shrink-0">
                {discovery.most_celebrated_artist.avatar ? (
                  <img src={discovery.most_celebrated_artist.avatar} alt={discovery.most_celebrated_artist.full_name} className="w-full h-full object-cover" />
                ) : (
                  <Users className="w-12 h-12 text-gold-400" strokeWidth={1} />
                )}
              </div>
              <div className="text-center sm:text-left flex-1">
                <h3 className="font-serif text-3xl font-semibold text-white">{discovery.most_celebrated_artist.full_name}</h3>
                <p className="text-gold-400 text-sm mt-1">{(discovery.most_celebrated_artist.categories || [])[0] || 'Artist'}{discovery.most_celebrated_artist.location ? ` · ${discovery.most_celebrated_artist.location}` : ''}</p>
                {discovery.most_celebrated_artist.bio && <p className="text-white/60 text-sm mt-3 line-clamp-2">{discovery.most_celebrated_artist.bio}</p>}
                <Link to={`/artist/${discovery.most_celebrated_artist.id}`} className="btn-gold mt-5 inline-flex" data-testid="celebrated-artist-link">View Profile <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Discover: New Artists + Recently Sold */}
      {(( discovery.recent_artists && discovery.recent_artists.length > 0) || (discovery.recent_solds && discovery.recent_solds.length > 0)) && (
        <section className="py-16 sm:py-20 bg-cream-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
            {discovery.recent_artists && discovery.recent_artists.length > 0 && (
              <div>
                <span className="eyebrow">Fresh Talent</span>
                <h2 className="mt-4 font-serif text-3xl font-semibold text-navy-800">Recently Onboarded Artists</h2>
                <div className="mt-8 space-y-3">
                  {discovery.recent_artists.slice(0, 5).map((a) => (
                    <Link key={a.id} to={`/artist/${a.id}`} className="card-classy flex items-center gap-4 p-3" data-testid={`recent-artist-${a.id}`}>
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-navy-800 text-gold-400 flex items-center justify-center shrink-0">
                        {a.avatar ? <img src={a.avatar} alt={a.full_name} className="w-full h-full object-cover" /> : <Users className="w-5 h-5" strokeWidth={1.6} />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-serif text-lg font-semibold text-navy-800 truncate">{a.full_name}</p>
                        <p className="text-xs text-navy-400">{(a.categories || [])[0] || 'Artist'}{a.location ? ` · ${a.location}` : ''}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gold-500 ml-auto" strokeWidth={2} />
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {discovery.recent_solds && discovery.recent_solds.length > 0 && (
              <div>
                <span className="eyebrow">Just Collected</span>
                <h2 className="mt-4 font-serif text-3xl font-semibold text-navy-800">Recently Sold</h2>
                <div className="mt-8 space-y-3">
                  {discovery.recent_solds.slice(0, 5).map((s) => (
                    <div key={s.order_id} className="card-classy flex items-center gap-4 p-3" data-testid={`recent-sold-${s.order_id}`}>
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-cream-200 shrink-0">
                        {s.artwork && (s.artwork.image || (s.artwork.images || [])[0]) ? (
                          <img src={s.artwork.image || s.artwork.images[0]} alt={s.artwork.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-navy-200" strokeWidth={1.2} /></div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-serif text-lg font-semibold text-navy-800 truncate">{s.artwork?.title || 'Artwork'}</p>
                        <p className="text-xs text-navy-400">Sold {s.created_at ? new Date(s.created_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }) : ''}</p>
                      </div>
                      <span className="text-sm font-semibold text-gold-600">₹{Number(s.amount || 0).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}


      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Simple Process"
            title="How ChitraKalakar Works"
            subtitle="Four graceful steps to begin your art journey"
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {howItWorks.map((item) => (
              <div key={item.step} className="card-classy p-7 text-center">
                <div className="flex items-center justify-center mb-5">
                  <div className="w-16 h-16 rounded-full bg-navy-800 flex items-center justify-center ring-4 ring-gold-500/15">
                    <item.Icon className="w-7 h-7 text-gold-400" strokeWidth={1.5} />
                  </div>
                </div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-600 mb-1">Step {item.step}</p>
                <h3 className="font-serif text-2xl font-semibold text-navy-800 mb-2">{item.title}</h3>
                <p className="text-sm text-navy-500">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join the Community */}
      <section className="py-16 sm:py-20 bg-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Belong Somewhere"
            title="Join the Community"
            subtitle="Connect with fellow artists across locations and styles"
          />
          {communities.length > 0 ? (
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {communities.slice(0, 6).map((community) => (
                <div key={community.id} className="card-classy p-6" data-testid={`community-card-${community.id}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-navy-800 flex items-center justify-center text-gold-400">
                      <Users className="w-5 h-5" strokeWidth={1.6} />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-semibold text-navy-800">{community.name}</h3>
                      {community.location && (
                        <p className="text-sm text-navy-400 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" strokeWidth={1.6} /> {community.location}
                        </p>
                      )}
                    </div>
                  </div>
                  {community.description && (
                    <p className="text-navy-500 text-sm mb-5 line-clamp-2">{community.description}</p>
                  )}
                  <button
                    onClick={() => handleJoinCommunity(community.id)}
                    className="btn-navy-outline w-full !py-2.5 text-sm"
                    data-testid={`join-community-${community.id}`}
                  >
                    Join Community
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-12 text-center py-14 card-classy">
              <div className="w-16 h-16 mx-auto rounded-full bg-gold-500/10 flex items-center justify-center mb-4">
                <Handshake className="w-7 h-7 text-gold-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-navy-800 mb-2">Communities Coming Soon</h3>
              <p className="text-navy-500 mb-5">Artists can create and join communities to connect and collaborate</p>
              {isAuthenticated && (
                <Link to="/dashboard" className="btn-gold">Create a Community</Link>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Featured Artists */}
      {featuredArtists.length > 0 && (
        <section className="py-16 sm:py-20 bg-cream-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-between items-end gap-4 mb-12">
              <div>
                <span className="eyebrow">Curated Talent</span>
                <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-800">Featured Artists</h2>
                <p className="mt-3 text-navy-500">Discover extraordinary talent from our community</p>
              </div>
              <Link to="/artists" className="btn-navy-outline !py-2 text-sm">View All Artists <ArrowRight className="w-4 h-4" /></Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
              {featuredArtists.map((artist) => (
                <Link
                  key={artist.id}
                  to={artist.artist_id ? `/artist/${artist.artist_id}` : `/featured-artist/${artist.id}`}
                  className="card-classy overflow-hidden group"
                >
                  <div className="relative h-52 bg-cream-200 flex items-center justify-center overflow-hidden">
                    {artist.avatar ? (
                      <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <Users className="w-16 h-16 text-navy-200" strokeWidth={1} />
                    )}
                    <div className="absolute top-3 left-3 bg-navy-800 text-gold-400 text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" strokeWidth={2} fill="currentColor" /> Featured
                    </div>
                    {artist.rating > 0 && (
                      <div className="absolute top-3 right-3 bg-white/95 rounded-full px-2.5 py-1 flex items-center gap-1 shadow">
                        <Star className="w-3.5 h-3.5 text-gold-500" fill="currentColor" strokeWidth={0} />
                        <span className="font-semibold text-xs text-navy-800">{artist.rating}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif text-xl font-semibold text-navy-800 group-hover:text-gold-600 transition-colors">{artist.name}</h3>
                    <p className="text-sm text-gold-600 font-medium mb-2">{artist.category || (artist.categories || [])[0] || 'Artist'}</p>
                    <div className="flex items-center gap-1.5 text-xs text-navy-400">
                      <MapPin className="w-3.5 h-3.5" strokeWidth={1.6} /> {artist.location || 'India'}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Art Categories */}
      <section className="py-16 sm:py-20 bg-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Explore Mediums" title="Art Categories" subtitle="Explore diverse art forms and mediums" />
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {ART_CATEGORIES.map((category, index) => (
              <Link
                key={index}
                to="/paintings"
                className="group card-classy px-6 py-6 text-center hover:border-gold-500/40"
              >
                <p className="font-serif text-lg font-semibold text-navy-800 group-hover:text-gold-600 transition-colors">{category}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Exhibitions */}
      {exhibitions.length > 0 && (
        <section className="py-16 sm:py-20 bg-cream-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-between items-end gap-4 mb-12">
              <div>
                <span className="eyebrow">Now Showing</span>
                <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-800">Virtual Exhibitions</h2>
                <p className="mt-3 text-navy-500">Explore curated art exhibitions from talented artists</p>
              </div>
              <Link to="/exhibitions" className="btn-navy-outline !py-2 text-sm">View All <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {exhibitions.slice(0, 6).map((exhibition) => (
                <div key={exhibition.id} className="card-classy overflow-hidden">
                  <div className="h-44 bg-navy-800 relative flex items-center justify-center overflow-hidden">
                    {(exhibition.primary_exhibition_image || (exhibition.exhibition_images || [])[0]) ? (
                      <img src={exhibition.primary_exhibition_image || exhibition.exhibition_images[0]} alt={exhibition.name} className="w-full h-full object-cover" />
                    ) : (
                      <Landmark className="w-12 h-12 text-gold-400/40" strokeWidth={1} />
                    )}
                    <div className="absolute top-3 right-3 bg-white/95 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider text-gold-700">
                      {exhibition.status}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif text-xl font-semibold text-navy-800 mb-1">{exhibition.name}</h3>
                    <p className="text-sm text-navy-400">by {exhibition.artist_name || 'Artist'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative py-20 sm:py-24 bg-navy-800 text-white overflow-hidden">
        <div className="absolute top-[-4rem] right-[-4rem] w-80 h-80 bg-gold-500/10 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span className="eyebrow justify-center !text-gold-400">Begin Your Journey</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl font-semibold mb-5">Ready to Create or Commission?</h2>
          <p className="text-lg text-white/70 mb-10">Join our thriving community of artists and art enthusiasts today.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="btn-gold" data-testid="cta-signup-btn">Create Free Account <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/login" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-6 py-3 font-semibold tracking-wide text-white hover:bg-white/10 transition-colors" data-testid="cta-login-btn">Sign In</Link>
          </div>
          <p className="mt-8 text-sm text-white/50">No credit card required • Free to join • Transparent pricing</p>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
